import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, 
  Loader2, 
  AlertCircle, 
  CheckCircle,
  ArrowRight,
  Shield,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import PortalFooter from '../components/PortalFooter';
import { api } from '../lib/api';

// Steps in the setup flow
const STEPS = {
  EMAIL: 'email',
  VERIFY: 'verify',
  SUCCESS: 'success'
};

export default function AccountSetupPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  
  // State
  const [step, setStep] = useState(STEPS.EMAIL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [policyInfo, setPolicyInfo] = useState(null);
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  // Refs for OTP inputs
  const otpRefs = useRef([]);

  // Redirect if already authenticated (e.g., user navigates back to this page)
  useEffect(() => {
    // Only redirect if authenticated AND not in the success step (to avoid double redirects)
    if (isAuthenticated && step !== STEPS.SUCCESS) {
      console.log('[AccountSetup] Already authenticated, redirecting to portal...');
      const portalUrl = `${window.location.origin}/portal`;
      window.location.replace(portalUrl);
    }
  }, [isAuthenticated, step]);

  // Resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (step === STEPS.VERIFY) {
      setCanResend(true);
    }
  }, [resendTimer, step]);

  // Step 1: Verify email has a policy
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Check if email has a policy and send verification code using axios
      const response = await api.post('/auth/setup/initiate', { email: email.trim() });
      
      setPolicyInfo(response.data.policy_info);
      setStep(STEPS.VERIFY);
      setResendTimer(60);
      setCanResend(false);
      
    } catch (err) {
      console.error('Setup initiate error:', err);
      
      // Handle axios error response
      if (err.response) {
        const errorMessage = err.response.data?.detail || 'Unable to verify email';
        if (errorMessage.toLowerCase().includes('unable to locate')) {
          setError('We were unable to locate a policy with this email. Please contact support.');
        } else {
          setError(errorMessage);
        }
      } else if (err.request) {
        setError('Connection error. Please check your internet and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
      
      if (newOtp.every(d => d !== '')) {
        handleOtpSubmit(newOtp.join(''));
      }
    } else {
      const newOtp = [...otp];
      newOtp[index] = value.replace(/\D/g, '');
      setOtp(newOtp);
      
      if (value && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
      
      if (newOtp.every(d => d !== '')) {
        handleOtpSubmit(newOtp.join(''));
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Step 2: Verify OTP and activate account
  const handleOtpSubmit = async (otpCode) => {
    const code = otpCode || otp.join('');
    
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/auth/setup/verify', {
        email: email.trim(),
        code: code
      });
      
      const data = response.data;
      
      console.log('[AccountSetup] Verification successful');
      console.log('[AccountSetup] Policies:', data.policies);
      
      // Store credentials in localStorage FIRST (synchronously)
      try {
        localStorage.setItem('losspay_token', data.token);
        localStorage.setItem('losspay_policies', JSON.stringify(data.policies));
        localStorage.setItem('losspay_remember_me', 'true');
        
        // Force a synchronous read to verify storage worked
        const storedToken = localStorage.getItem('losspay_token');
        const storedPolicies = localStorage.getItem('losspay_policies');
        
        console.log('[AccountSetup] Storage verification:');
        console.log('[AccountSetup] - Token stored:', !!storedToken);
        console.log('[AccountSetup] - Policies stored:', storedPolicies);
        
        if (!storedToken) {
          throw new Error('Failed to store authentication token');
        }
      } catch (storageError) {
        console.error('[AccountSetup] localStorage error:', storageError);
        setError('Unable to save login credentials. Please try again or use a different browser.');
        return;
      }
      
      // Update auth context state
      login(data.token, data.policies, email, data.policies[0], true);
      
      // Show success state
      setStep(STEPS.SUCCESS);
      
      // Prepare redirect URL
      const portalUrl = `${window.location.origin}/portal`;
      console.log('[AccountSetup] Will redirect to:', portalUrl);
      
      // Use a longer delay and verify before redirect
      setTimeout(() => {
        // Double-check token still exists before redirect
        const tokenCheck = localStorage.getItem('losspay_token');
        console.log('[AccountSetup] Pre-redirect token check:', !!tokenCheck);
        
        if (tokenCheck) {
          console.log('[AccountSetup] Executing redirect...');
          window.location.href = portalUrl;
        } else {
          console.error('[AccountSetup] Token was cleared before redirect!');
          setError('Session was interrupted. Please click "Go to Dashboard" button.');
          setStep(STEPS.SUCCESS); // Keep on success page so user can manually navigate
        }
      }, 2000);  // Increased to 2 seconds for reliability
      
    } catch (err) {
      console.error('[AccountSetup] Verify error:', err);
      let errorMessage = 'Verification failed';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      } else if (!err.response) {
        errorMessage = 'Unable to connect to server. Please check your connection.';
      }
      
      setError(errorMessage);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const handleResend = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await api.post('/auth/setup/initiate', { email: email.trim() });
      
      setResendTimer(60);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to resend code';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8EEFF] dark:bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200/80 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-center h-16">
            <a href="https://losspay.com" className="flex-shrink-0">
              <img 
                src="https://images.losspay.com/losspay/losspay-logo.png" 
                alt="LossPay" 
                className="h-8 md:h-9"
              />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          
          {/* Badge */}
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-slate-800/80 rounded-full text-sm font-medium text-[#3B5BDB]">
              <span className="w-2 h-2 bg-[#3B5BDB] rounded-full"></span>
              Account Activation
            </span>
          </div>

          {/* Email Step */}
          {step === STEPS.EMAIL && (
            <Card className="shadow-xl border-0 rounded-2xl dark:bg-slate-800">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-[#E8EEFF] dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-[#3B5BDB]" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    Activate Your <span className="text-[#3B5BDB]">Account</span>
                  </h1>
                  <p className="text-slate-600 dark:text-slate-400">
                    Enter the email address you used when purchasing your policy.
                  </p>
                </div>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-red-700">{error}</p>
                      {error.includes('unable to locate') && (
                        <p className="text-sm text-red-600 mt-2">
                          Need help? Contact{' '}
                          <a href="mailto:support@losspay.com" className="underline font-medium">
                            support@losspay.com
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleEmailSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12 pl-12 rounded-xl border-slate-200 focus:border-[#3B5BDB] focus:ring-[#3B5BDB]/20"
                        autoComplete="email"
                        autoFocus
                        data-testid="setup-email-input"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl font-semibold text-base"
                    disabled={loading}
                    data-testid="setup-continue-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
                
                <div className="mt-6 pt-6 border-t border-slate-200 text-center">
                  <p className="text-sm text-slate-500">
                    Already have an account?{' '}
                    <a 
                      href="https://secure.losspay.com" 
                      className="text-[#3B5BDB] hover:underline font-semibold"
                    >
                      Sign in
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Verify Step */}
          {step === STEPS.VERIFY && (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-[#E8EEFF] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Lock className="h-8 w-8 text-[#3B5BDB]" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">
                    Verify Your <span className="text-[#3B5BDB]">Identity</span>
                  </h1>
                  <p className="text-slate-600">
                    We sent a 6-digit code to<br />
                    <span className="font-semibold text-slate-900">{email}</span>
                  </p>
                </div>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
                
                <div className="space-y-6">
                  {/* OTP Input */}
                  <div className="flex justify-center gap-2">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={el => otpRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-200 rounded-xl focus:border-[#3B5BDB] focus:ring-2 focus:ring-[#3B5BDB]/20 outline-none transition-all"
                        autoFocus={index === 0}
                        data-testid={`otp-input-${index}`}
                      />
                    ))}
                  </div>
                  
                  <Button 
                    onClick={() => handleOtpSubmit()}
                    className="w-full h-12 bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl font-semibold"
                    disabled={loading || otp.some(d => !d)}
                    data-testid="setup-verify-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Activate Account
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </>
                    )}
                  </Button>
                  
                  {/* Resend */}
                  <div className="text-center">
                    {canResend ? (
                      <button
                        onClick={handleResend}
                        disabled={loading}
                        className="text-sm text-[#3B5BDB] hover:underline font-medium"
                      >
                        Resend code
                      </button>
                    ) : (
                      <p className="text-sm text-slate-500">
                        Resend code in {resendTimer}s
                      </p>
                    )}
                  </div>
                  
                  {/* Back */}
                  <div className="text-center pt-4 border-t border-slate-200">
                    <button
                      onClick={() => {
                        setStep(STEPS.EMAIL);
                        setOtp(['', '', '', '', '', '']);
                        setError(null);
                      }}
                      className="text-sm text-slate-600 hover:text-slate-900"
                    >
                      ← Use a different email
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Success Step */}
          {step === STEPS.SUCCESS && (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardContent className="p-8">
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">
                    Account <span className="text-[#3B5BDB]">Activated!</span>
                  </h1>
                  <p className="text-slate-600 mb-6">
                    Your account is ready. Redirecting to your dashboard...
                  </p>
                  <Loader2 className="h-6 w-6 animate-spin text-[#3B5BDB] mx-auto mb-6" />
                  
                  {/* Fallback button in case auto-redirect fails */}
                  <Button
                    onClick={() => window.location.href = `${window.location.origin}/portal`}
                    className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Security Note */}
          <p className="text-xs text-slate-500 text-center mt-6 px-4">
            Your information is protected using secure encryption and industry standard safeguards.
          </p>
          
        </div>
      </main>

      {/* Footer */}
      <PortalFooter />
    </div>
  );
}
